import { Action, createReducer, on } from '@ngrx/store';
import { ErrorStore } from '@sleipai/sleip-models';

import * as NotificationActions from '../actions/notification.actions';
import {
  activeRecordingNotificationAdapter,
  ActiveRecordingNotificationState,
} from '../entity/active-recording-notification.entity';
import {
  unreadNotificationCountsByHorseAdapter,
  UnreadNotificationCountsByHorseState,
} from '../entity/unread-notification-counts-by-horse.entity';
import {
  notificationsForRecordingsUnreadAdapter,
  NotificationsForRecordingsUnreadState,
} from '../entity/notifications-for-recordings-unread.entity';

export const notificationsFeatureKey = 'notifications';

export interface State {
  // active recording notification document
  activeRecordingNotifications: ActiveRecordingNotificationState;
  watchedRecordingId: string | null;
  loading: boolean;
  loaded: boolean;
  markingRead: boolean;
  error: ErrorStore | null;

  // this user's unread recordings for the horse currently being viewed
  notificationsForRecordingsUnread: NotificationsForRecordingsUnreadState;
  notificationsForRecordingsUnreadLoading: boolean;
  notificationsForRecordingsUnreadLoaded: boolean;
  notificationsForRecordingsUnreadError: ErrorStore | null;

  // per-horse unread counts
  unreadNotificationCountsByHorse: UnreadNotificationCountsByHorseState;
  unreadNotificationCountsLoading: boolean;
  unreadNotificationCountsLoaded: boolean;
  unreadNotificationCountsError: ErrorStore | null;
}

export const initialState: State = {
  activeRecordingNotifications: activeRecordingNotificationAdapter.getInitialState(),
  watchedRecordingId: null,
  loading: false,
  loaded: false,
  markingRead: false,
  error: null,

  notificationsForRecordingsUnread:
    notificationsForRecordingsUnreadAdapter.getInitialState(),
  notificationsForRecordingsUnreadLoading: false,
  notificationsForRecordingsUnreadLoaded: false,
  notificationsForRecordingsUnreadError: null,

  unreadNotificationCountsByHorse: unreadNotificationCountsByHorseAdapter.getInitialState(),
  unreadNotificationCountsLoading: false,
  unreadNotificationCountsLoaded: false,
  unreadNotificationCountsError: null,
};

const toErrorStore = (error: any): ErrorStore => ({
  message: error?.message ?? String(error),
  status: error?.status ?? error?.code,
});

const adjustUnreadCount = (
  slice: UnreadNotificationCountsByHorseState,
  horseId: string,
  delta: number
): UnreadNotificationCountsByHorseState => {
  const current = slice.entities[horseId];

  if (!current && delta < 0) {
    return slice;
  }

  return unreadNotificationCountsByHorseAdapter.upsertOne(
    {
      horseId,
      latestUnreadAt: null,
      lastUpdatedAt: null,
      createdAt: null,
      ...current,
      unreadCount: Math.max(0, (current?.unreadCount ?? 0) + delta),
    },
    slice
  );
};

const applyReadStatus = (
  state: State,
  recordingId: string,
  isRead: boolean
): Partial<State> => {
  const notification = state.activeRecordingNotifications.entities[recordingId];
  const wasRead = notification?.viewStatus ?? true;

  if (!notification || wasRead === isRead) {
    return {};
  }

  const updated = { ...notification, viewStatus: isRead };
  const horseId = notification.metadata?.horseId;

  return {
    unreadNotificationCountsByHorse: horseId
      ? adjustUnreadCount(
          state.unreadNotificationCountsByHorse,
          horseId,
          isRead ? -1 : 1
        )
      : state.unreadNotificationCountsByHorse,
    activeRecordingNotifications: activeRecordingNotificationAdapter.upsertOne(
      updated,
      state.activeRecordingNotifications
    ),
    notificationsForRecordingsUnread: isRead
      ? notificationsForRecordingsUnreadAdapter.removeOne(
          recordingId,
          state.notificationsForRecordingsUnread
        )
      : notificationsForRecordingsUnreadAdapter.upsertOne(
          updated,
          state.notificationsForRecordingsUnread
        ),
  };
};

export const notificationsReducer = createReducer(
  initialState,

  on(
    NotificationActions.watchActiveRecordingNotification,
    (state, { recordingId }) => ({
      ...state,
      loading: true,
      error: null,
      watchedRecordingId: recordingId,
    })
  ),

  on(
    NotificationActions.watchActiveRecordingNotificationSuccess,
    (state, { recordingId, notification }) => ({
      ...state,
      activeRecordingNotifications: notification
        ? activeRecordingNotificationAdapter.upsertOne(
            notification,
            state.activeRecordingNotifications
          )
        : activeRecordingNotificationAdapter.removeOne(
            recordingId,
            state.activeRecordingNotifications
          ),
      loading: false,
      loaded: true,
    })
  ),

  on(
    NotificationActions.watchActiveRecordingNotificationFailure,
    (state, { error }) => ({
      ...state,
      loading: false,
      loaded: true,
      error: toErrorStore(error),
    })
  ),

  on(NotificationActions.stopWatchingActiveRecordingNotification, (state) => ({
    ...state,
    watchedRecordingId: null,
    loading: false,
  })),
  on(
    NotificationActions.markRecordingNotificationRead,
    (state, { recordingId, read }) => ({
      ...state,
      ...applyReadStatus(state, recordingId, read ?? true),
      markingRead: true,
      error: null,
    })
  ),

  on(NotificationActions.markRecordingNotificationReadSuccess, (state) => ({
    ...state,
    markingRead: false,
  })),
  on(
    NotificationActions.markRecordingNotificationReadFailure,
    (state, { recordingId, read, error }) => ({
      ...state,
      ...applyReadStatus(state, recordingId, !(read ?? true)),
      markingRead: false,
      error: toErrorStore(error),
    })
  ),
  on(NotificationActions.watchNotificationsForRecordingsUnread, (state) => ({
    ...state,
    notificationsForRecordingsUnreadLoading: true,
    notificationsForRecordingsUnreadError: null,
  })),
  on(
    NotificationActions.watchNotificationsForRecordingsUnreadSuccess,
    (state, { recordingIds, notifications }) => {
      const stillUnread = new Set(
        notifications.map((notification) => notification.resourceId)
      );
      const nowRead = recordingIds.filter(
        (recordingId) => !stillUnread.has(recordingId)
      );

      return {
        ...state,
        notificationsForRecordingsUnread:
          notificationsForRecordingsUnreadAdapter.upsertMany(
            notifications,
            notificationsForRecordingsUnreadAdapter.removeMany(
              nowRead,
              state.notificationsForRecordingsUnread
            )
          ),
        notificationsForRecordingsUnreadLoading: false,
        notificationsForRecordingsUnreadLoaded: true,
      };
    }
  ),
  on(
    NotificationActions.watchNotificationsForRecordingsUnreadFailure,
    (state, { error }) => ({
      ...state,
      notificationsForRecordingsUnreadLoading: false,
      notificationsForRecordingsUnreadLoaded: true,
      notificationsForRecordingsUnreadError: toErrorStore(error),
    })
  ),
  on(
    NotificationActions.stopWatchingNotificationsForRecordingsUnread,
    (state) => ({
      ...state,
      notificationsForRecordingsUnread:
        notificationsForRecordingsUnreadAdapter.removeAll(
          state.notificationsForRecordingsUnread
        ),
      notificationsForRecordingsUnreadLoading: false,
    })
  ),
  on(NotificationActions.watchUnreadNotificationCountsByHorse, (state) => ({
    ...state,
    unreadNotificationCountsLoading: true,
    unreadNotificationCountsError: null,
  })),
  on(
    NotificationActions.watchUnreadNotificationCountsByHorseSuccess,
    (state, { horseIds, unreadNotificationCountsByHorse }) => {
      const stillUnread = new Set(
        unreadNotificationCountsByHorse.map((count) => count.horseId)
      );
      const nowRead = horseIds.filter((horseId) => !stillUnread.has(horseId));

      return {
        ...state,
        unreadNotificationCountsByHorse:
          unreadNotificationCountsByHorseAdapter.upsertMany(
            unreadNotificationCountsByHorse,
            unreadNotificationCountsByHorseAdapter.removeMany(
              nowRead,
              state.unreadNotificationCountsByHorse
            )
          ),
        unreadNotificationCountsLoading: false,
        unreadNotificationCountsLoaded: true,
      };
    }
  ),
  on(
    NotificationActions.watchUnreadNotificationCountsByHorseFailure,
    (state, { error }) => ({
      ...state,
      unreadNotificationCountsLoading: false,
      unreadNotificationCountsLoaded: true,
      unreadNotificationCountsError: toErrorStore(error),
    })
  ),
  on(
    NotificationActions.stopWatchingUnreadNotificationCountsByHorse,
    (state) => ({
      ...state,
      unreadNotificationCountsByHorse:
        unreadNotificationCountsByHorseAdapter.removeAll(
          state.unreadNotificationCountsByHorse
        ),
      unreadNotificationCountsLoading: false,
    })
  ),
  on(NotificationActions.clearNotificationsError, (state) => ({
    ...state,
    error: null,
    notificationsForRecordingsUnreadError: null,
    unreadNotificationCountsError: null,
  }))
);

export function reducer(state: State | undefined, action: Action) {
  return notificationsReducer(state, action);
}
